import format from "date-fns/format"
import parseISO from "date-fns/parseISO"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"

import BasePopover from "src/components/BasePopover"
import Button from "src/components/Button"
import Chips from "src/components/Chips"
import Form from "src/components/Form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "src/components/Select"

import { useTracker } from "src/hooks/use_tracker"

const STATUS_OPTIONS = [
  { value: "past_due", label: "Overdue" },
  { value: "failed", label: "Failed" },
  { value: "settled", label: "Settled" },
  { value: "processing", label: "Processing" },
  { value: "disputed", label: "Disputed" },
  { value: "scheduled", label: "Scheduled" },
]

const ContactInvoicesFilters = ({ table, onExportSummary, actions }) => {
  const tracker = useTracker()

  const [startDate, setStartDate] = useState(
    table.filters.due_date_gteq ? parseISO(table.filters.due_date_gteq) : null
  )
  const [endDate, setEndDate] = useState(
    table.filters.due_date_lteq ? parseISO(table.filters.due_date_lteq) : null
  )

  useEffect(() => {
    setStartDate(
      table.filters.due_date_gteq ? parseISO(table.filters.due_date_gteq) : null
    )
    setEndDate(
      table.filters.due_date_lteq ? parseISO(table.filters.due_date_lteq) : null
    )
  }, [table.filters.due_date_gteq, table.filters.due_date_lteq])

  const handleStatusChange = (values) => {
    if (values.length > 0) {
      tracker.trackEvent("customer_invoices_tab:status_filter_pressed", {
        statuses: values,
      })
    }
    table.setFilter("statuses", values)
  }

  const handleDateChange = (field, date) => {
    const formatted = date ? format(date, "yyyy-MM-dd") : ""

    if (field === "due_date_gteq") {
      setStartDate(date)
    } else {
      setEndDate(date)
    }
    table.setFilter(field, formatted)

    if (formatted) {
      tracker.trackEvent("customer_invoices_tab:due_date_filter_pressed", {
        start_date:
          field === "due_date_gteq" ? formatted : table.filters.due_date_gteq,
        end_date:
          field === "due_date_lteq" ? formatted : table.filters.due_date_lteq,
      })
    }
  }

  const buildChips = () => {
    const chips = []

    ;(table.filters.statuses || []).forEach((status) => {
      chips.push({
        key: `status-${status}`,
        text: STATUS_OPTIONS.find((o) => o.value === status)?.label || status,
        onClick: () =>
          table.setFilter(
            "statuses",
            table.filters.statuses.filter((s) => s !== status)
          ),
      })
    })

    if (table.filters.due_date_gteq || table.filters.due_date_lteq) {
      const start = table.filters.due_date_gteq
        ? format(parseISO(table.filters.due_date_gteq), "MM/dd/yyyy")
        : "—"
      const end = table.filters.due_date_lteq
        ? format(parseISO(table.filters.due_date_lteq), "MM/dd/yyyy")
        : "—"
      chips.push({
        key: "due-date",
        text: `Due: ${start} – ${end}`,
        onClick: () => {
          setStartDate(null)
          setEndDate(null)
          table.setFilters({ due_date_gteq: "", due_date_lteq: "" })
        },
      })
    }

    return chips
  }

  const chips = buildChips()

  return (
    <div>
      <div className="no-print flex w-full flex-wrap items-center justify-between gap-2 pt-1">
        <div className="flex gap-2">
          <Select
            multiple
            value={table.filters.statuses || []}
            onValueChange={handleStatusChange}
          >
            <SelectTrigger variant="button">Status</SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <BasePopover label="Due Date">
            <div className="space-y-2 p-4">
              <div className="flex flex-col">
                <Form.Label htmlFor="due_date_gteq">Start Date</Form.Label>
                <Form.DatePicker
                  id="due_date_gteq"
                  value={startDate}
                  onChange={(date) => handleDateChange("due_date_gteq", date)}
                  maxDate={endDate}
                  todayButton="Today"
                  portalId={null}
                />
              </div>
              <div className="flex flex-col">
                <Form.Label htmlFor="due_date_lteq">End Date</Form.Label>
                <Form.DatePicker
                  id="due_date_lteq"
                  value={endDate}
                  onChange={(date) => handleDateChange("due_date_lteq", date)}
                  minDate={startDate}
                  todayButton="Today"
                  portalId={null}
                />
              </div>
            </div>
          </BasePopover>
        </div>
        {actions ?? (
          <Button variant="secondary" onClick={onExportSummary}>
            Export Summary
          </Button>
        )}
      </div>

      {chips.length > 0 && (
        <Chips
          onReset={() => {
            setStartDate(null)
            setEndDate(null)
            table.clearAllFilters()
          }}
        >
          {chips.map((chip) => (
            <Chips.Chip
              key={chip.key}
              text={chip.text}
              onClick={chip.onClick}
            />
          ))}
        </Chips>
      )}
    </div>
  )
}

ContactInvoicesFilters.propTypes = {
  table: PropTypes.object.isRequired,
  onExportSummary: PropTypes.func.isRequired,
  actions: PropTypes.node,
}

export default ContactInvoicesFilters
